import {
  useCreateUserMutation,
  useGetPortfolioQuery,
  useGetPortfoliosQuery,
} from '@/api/portfolioApi';
import SuspenseLoading from '@/components/common/SuspenseLoading';
import {
  InviteUserModal,
  CreatePortfolio,
  SchedulerHistoryModal,
} from '@/components/widgets/Portfolio';
import { SelectPortfolioModal } from '@/components/widgets/Portfolios';
import { VersionInfoModal } from '@/components/widgets/VersionInfo';
import { USER_ROLE } from '@/constants';
import {
  selectIsGlobalAdmin,
  selectPortfolioId,
} from '@/features/auth/authSelector';
import { useKeycloak } from '@/providers/KeycloakProvider';
import {
  BookOutlined,
  HistoryOutlined,
  ClockCircleOutlined,
  HomeOutlined,
  InfoCircleOutlined,
  LogoutOutlined,
  SettingOutlined,
  UserOutlined,
  LineChartOutlined,
  DollarOutlined,
} from '@ant-design/icons';
import { showSuccess } from '@/utils/messageService';
import { FloatButton, Form, Layout } from 'antd';
import { Suspense, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import HeaderBar from './Header';
import './MainLayout.less';
import Navbar from './Navbar';

const { Content } = Layout;

const MainLayout = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [scrollContainer, setScrollContainer] = useState(null);
  const [portfolioModalOpen, setPortfolioModalOpen] = useState(false);
  const [createPortfolioOpen, setCreatePortfolioOpen] = useState(false);
  const [createAdminOpen, setCreateAdminOpen] = useState(false);
  const [schedulerHistoryOpen, setSchedulerHistoryOpen] = useState(false);
  const [versionInfoOpen, setVersionInfoOpen] = useState(false);
  const [globalAdminForm] = Form.useForm();
  const [createUser, { isLoading: isCreatingUser }] = useCreateUserMutation();
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useKeycloak();

  const isGlobalAdmin = useSelector(selectIsGlobalAdmin);
  const portfolioId = useSelector(selectPortfolioId);

  const isOnPortfolioPage = location.pathname.startsWith('/portfolios/');

  // Extract portfolio ID from URL if on portfolio page
  const urlPortfolioId = useMemo(() => {
    if (isOnPortfolioPage) {
      const pathSegments = location.pathname.split('/').filter(Boolean);
      const portfoliosIndex = pathSegments.indexOf('portfolios');
      if (portfoliosIndex >= 0 && pathSegments.length > portfoliosIndex + 1) {
        return pathSegments[portfoliosIndex + 1];
      }
    }
    return null;
  }, [location.pathname, isOnPortfolioPage]);

  // For global admins, fetch the full portfolios list
  const { data: portfoliosResponse } = useGetPortfoliosQuery(
    { size: 0 },
    {
      skip: !isGlobalAdmin,
    }
  );
  const portfoliosList = portfoliosResponse?.data || portfoliosResponse || [];

  // For non-global admins on portfolio page, fetch individual portfolio
  const { data: singlePortfolio } = useGetPortfolioQuery(urlPortfolioId, {
    skip: isGlobalAdmin || !urlPortfolioId,
  });

  // Create a map of portfolio ID to portfolio name for breadcrumb lookup
  const portfolioNameMap = useMemo(() => {
    if (isGlobalAdmin && portfoliosList?.length > 0) {
      return portfoliosList.reduce((acc, portfolio) => {
        acc[portfolio.portfolioId] = portfolio.portfolioName;
        return acc;
      }, {});
    }
    if (singlePortfolio && urlPortfolioId) {
      return { [urlPortfolioId]: singlePortfolio.portfolioName };
    }
    return {};
  }, [isGlobalAdmin, portfoliosList, singlePortfolio, urlPortfolioId]);

  const handleCreateGlobalAdmin = async (values) => {
    try {
      await createUser({
        email: values.email,
        firstName: values.firstName,
        lastName: values.lastName,
        role: USER_ROLE.GLOBAL_ADMIN,
      }).unwrap();
      globalAdminForm.resetFields();
      setCreateAdminOpen(false);
      showSuccess('Global Admin created successfully!');
    } catch (error) {
      console.error('Failed to create global admin:', error);
    }
  };

  const menuItems = [
    {
      key: '/facility-bids',
      icon: <DollarOutlined />,
      label: 'Facility Bids',
      onClick: () => navigate('/facility-bids'),
    },
    {
      key: '/insights',
      icon: <LineChartOutlined />,
      label: 'Insights',
      onClick: () => navigate('/insights'),
    },
    {
      key: '/settings',
      icon: <SettingOutlined />,
      label: 'Settings',
      onClick: () => navigate('/settings'),
    },
    ...(isGlobalAdmin
      ? [
          {
            key: '/create-portfolio',
            icon: <BookOutlined />,
            label: 'Create Portfolio',
            onClick: () => setCreatePortfolioOpen(true),
          },
          {
            key: '/create-global-admin',
            icon: <UserOutlined />,
            label: 'Create Global Admin',
            onClick: () => setCreateAdminOpen(true),
          },
          {
            key: '/scheduler-history',
            icon: <HistoryOutlined />,
            label: 'Scheduler History',
            onClick: () => setSchedulerHistoryOpen(true),
          },
          {
            key: '/schedulers',
            icon: <ClockCircleOutlined />,
            label: 'Schedulers',
            onClick: () => navigate('/schedulers'),
          },
          {
            key: '/version-info',
            icon: <InfoCircleOutlined />,
            label: 'Version Info',
            onClick: () => setVersionInfoOpen(true),
          },
        ]
      : []),
  ];

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profile',
      onClick: () => navigate('/profile'),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: logout,
      danger: true,
    },
  ];

  const getBreadcrumbItems = () => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const labelMap = {
      'facility-bids': 'Facility Bids',
      insights: 'Insights',
      schedulers: 'Schedulers',
      settings: 'Settings',
      portfolios: 'Portfolios',
      profile: 'Profile',
    };

    const items = [
      {
        title: <HomeOutlined />,
      },
    ];

    pathSegments.forEach((segment, index) => {
      const previousSegment = index > 0 ? pathSegments[index - 1] : null;

      const isPortfolioId = previousSegment === 'portfolios';

      let title;

      if (isPortfolioId && portfolioNameMap[segment]) {
        title = portfolioNameMap[segment];
      } else {
        title =
          labelMap[segment] || segment.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
      }

      items.push({
        title,
      });
    });

    return items;
  };

  const getSelectedMenuKey = () => {
    const currentPath = location.pathname;

    const matchingMenuItem = menuItems.find(
      (item) => currentPath === item.key || currentPath.startsWith(item.key + '/')
    );

    return matchingMenuItem ? matchingMenuItem.key : currentPath;
  };

  return (
    <Layout className="main-layout">
      <Navbar
        collapsed={collapsed}
        onToggleCollapsed={() => setCollapsed(!collapsed)}
        selectedKey={getSelectedMenuKey()}
        items={menuItems}
      />
      <Layout>
        <HeaderBar
          breadcrumbItems={getBreadcrumbItems()}
          userMenuItems={userMenuItems}
          showSelectPortfolio={isGlobalAdmin}
          onSelectPortfolio={() => setPortfolioModalOpen(true)}
        />
        <Content className="page-content" ref={setScrollContainer}>
          <Suspense fallback={<SuspenseLoading />} key={location.pathname}>
            <div className="content-area">
              <Outlet />
            </div>
          </Suspense>
        </Content>
      </Layout>

      <FloatButton.BackTop visibilityHeight={200} target={() => scrollContainer} />

      <CreatePortfolio
        visible={createPortfolioOpen}
        onCancel={() => setCreatePortfolioOpen(false)}
        onSuccess={() => setCreatePortfolioOpen(false)}
      />

      <InviteUserModal
        open={createAdminOpen}
        onCancel={() => {
          setCreateAdminOpen(false);
          globalAdminForm.resetFields();
        }}
        onSubmit={handleCreateGlobalAdmin}
        loading={isCreatingUser}
        form={globalAdminForm}
        showRole={false}
      />

      <SchedulerHistoryModal
        open={schedulerHistoryOpen}
        onCancel={() => setSchedulerHistoryOpen(false)}
        portfolioId={urlPortfolioId || portfolioId}
      />

      <VersionInfoModal open={versionInfoOpen} onCancel={() => setVersionInfoOpen(false)} />

      <SelectPortfolioModal
        open={portfolioModalOpen}
        onCancel={() => setPortfolioModalOpen(false)}
        onSelect={(portfolio) => {
          console.log('Selected portfolio:', portfolio);
          // TODO: Dispatch action to switch portfolio
        }}
        currentPortfolioId={portfolioId}
      />
    </Layout>
  );
};

export default MainLayout;
